import { z } from 'zod';
import {
  amountSchema,
  imageWithUploadSchema,
} from '../../common';
import { dateSchema, nameSchema, objectIdSchema } from '../../validation';
import { financingInvoiceSourceSchema } from './financing.enums';

/**
 * Discriminated invoice block on a `FinancingApplication`.
 *
 * - `source: 'hero'` — references an existing `Invoice._id`. The
 *   `_id` field carries the source doc's `ObjectId`; the
 *   `clientName` / `amount` / `expectedDueDate` are derived from
 *   the source Hero invoice at create time and snapshotted here
 *   so the lender packet is self-contained.
 * - `source: 'external'` — used by carriers (often brand-new
 *   signups) who upload a PDF + type client details. `document`
 *   is REQUIRED for this branch.
 *
 * Same downstream packet for both branches — `financingService`
 * reads from `invoice.clientName` / `invoice.amount` /
 * `invoice.expectedDueDate` without caring about the source.
 *
 * Naming note — this snapshot used to be called `customerName`
 * but the rest of the platform refers to the invoice payer as
 * `Client` (relations-v2 CRM noun), and the carrier-facing apply
 * form / lender packet already speak in those terms. The field
 * was renamed to `clientName` to keep schema and product copy
 * aligned.
 */
export const financingInvoiceSchema = z
  .object({
    source: financingInvoiceSourceSchema,

    /**
     * Source `Invoice._id`. Required for `source: 'hero'`.
     * Absent for `source: 'external'`.
     */
    _id: objectIdSchema.optional(),

    /**
     * Required reference to the carrier's `Client` (relations-v2 CRM)
     * representing the buyer on the invoice.
     *
     * - `source: 'external'` — set by the apply form when the carrier
     *   picks (or creates inline) a Client in the client-name slot.
     * - `source: 'hero'`     — resolved from the Hero invoice buyer's
     *   `client_id` party id at submit time.
     *
     * `clientName` is a denormalized snapshot for the lender packet;
     * this field is the canonical CRM link used for agreement lookup
     * and client-legal gates.
     */
    clientId: objectIdSchema,

    /**
     * Display name of the buyer on the invoice. For Hero invoices
     * this is derived from the source Invoice; for external invoices
     * it is derived from the selected Client (`client.name`) — the
     * apply form forbids free-text entry to keep CRM and financing
     * in sync.
     */
    clientName: nameSchema,

    /** Amount block in the document's currency. */
    amount: amountSchema,

    /** When the invoice is expected to be paid by the customer. */
    expectedDueDate: dateSchema,

    /**
     * Uploaded invoice document. REQUIRED for `source: 'external'`,
     * absent for `source: 'hero'` (where the Hero Invoice already
     * has its own document on file).
     *
     * Stored under the `financing_invoice_document` folder via the
     * existing image module.
     */
    document: imageWithUploadSchema.optional(),

    /** Free-text invoice number; useful for both branches. */
    invoiceNumber: z.string().trim().optional(),
  })
  .superRefine((data, ctx) => {
    // Schema-level invariant: external invoices MUST carry a document.
    if (data.source === 'external' && !data.document) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['document'],
        message: 'document is required when invoice.source is "external"',
      });
    }
    // Schema-level invariant: hero invoices MUST carry the source _id.
    if (data.source === 'hero' && !data._id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['_id'],
        message: '_id is required when invoice.source is "hero"',
      });
    }
  });

export type FinancingInvoice = z.infer<typeof financingInvoiceSchema>;
